<template>
  <div>
    <div class="row">
      <div class="col-12">
        <ActionDropMenu class="float-right">
          <DropdownMenuItem @click="exportDataCSV" icon="fa-file-csv">Export CSV</DropdownMenuItem>
        </ActionDropMenu>
        
        <ReportFilterPopover
          class="float-right mr-2"
          headerTitle="Order Value Filter"
          :showApplyButton="true"
          :showResetButton="true"
          @apply="applyOrderSummaryFilter"
          @reset="resetOrderSummaryFilter">
          <template v-slot:popover>
            <ReportDateRangeSelect 
              :startDate="editingStartDate"
              :endDate="editingEndDate"
              @updateStartDate="editingStartDate = $event"
              @updateEndDate="editingEndDate = $event"
            />
            <ReportFilterGroup label="Contact">
              <ReportContactSelect
                v-model="editingContactId"
                @updateName="editingContactName = $event"
              />
            </ReportFilterGroup>
            <ReportFilterGroup label="Account Location">
              <AccountLocationMultiSelect v-model="editingAccountLocationIdList" />
            </ReportFilterGroup>
          </template>
          <template v-slot:filterDisplay>
            <div>
              <i class="far fa-calendar mr-2"></i>{{startDate | formatDate}} - {{endDate | formatDate}}
            </div>
            <div v-if="contactId">
              <i class="far fa-user-circle mr-2"></i>{{contactName}}
            </div>
            <ReportFilterDisplayAccountLocation :accountLocationId="accountLocationId" />            
          </template>
        </ReportFilterPopover>
        <ContentTitle>{{title}}</ContentTitle>
        <ContentSubTitle>Based on orders created between {{formatDate(startDate)}} and {{formatDate(endDate)}}</ContentSubTitle>
        <ContentSubTitle>All values are {{taxName}} inclusive</ContentSubTitle>
      </div>
    </div>
    <div class="row mt-4">
      <div class="col-12">
        <PivotDataTable
          :dataset="dataset"
          :columns="columns"
          :rowFields="rowFields"
          :fields="fields"
          :defaultColumnOrderIndex="defaultColumnOrderIndex"
          defaultColumnOrderDir="DESC">
          <template v-slot:rowfield="info">
            <strong v-if="info.data.column.field[0] == 'name'">
              <LinkStock v-if="info.data.row.cellData.stockId" :name="info.data.row.cellData.name" :id="info.data.row.cellData.stockId" />
              <LinkContact v-else-if="info.data.row.cellData.contactId" :name="info.data.row.cellData.name" :id="info.data.row.cellData.contactId" />
              <span v-else>{{info.data.row.cellData.name}}</span>
            </strong>
          </template>
          <template v-slot:cell="cell">
            <span v-if="cell.data == 0">-</span>
            <span v-else-if="isCurrency">{{formatCurrency(cell.data)}}</span>
            <span v-else-if="isSquareMetres">{{formatDecimal(cell.data, 2, true)}} m&sup2;</span>
          </template>
          <template v-slot:empty>
            No data found
          </template>
        </PivotDataTable>

      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import buildUrl from 'build-url'

import PivotDataTable from '@/components/table/pivotDataTable'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'
import AccountLocationMultiSelect from '@/modules/admin/components/accountLocationMultiSelect'
import ReportContactSelect from '@/components/reporting/reportContactSelect'
import ReportFilterDisplayAccountLocation from '@/components/reporting/reportFilterDisplayAccountLocation'

export default {
  name: 'OrderValueSummaryList',
  components: {
    PivotDataTable,
    ReportFilterPopover,
    ReportDateRangeSelect,
    AccountLocationMultiSelect,
    ReportFilterDisplayAccountLocation,
    ReportFilterGroup,
    ReportContactSelect
  },
  props: {
    title: String,
    startDate: String,
    endDate: String,
    accountLocationId: [Array, Number],
    contactId: [String, Number],
    contactName: String,
    valueList: Array,
    monthList: Array,
    reportField: String,

    isCurrency: Boolean,
    isSquareMetres: Boolean
  },
  data() {
    return {
      editingStartDate: this.startDate,
      editingEndDate: this.endDate,
      editingAccountLocationIdList: this.accountLocationId || [],
      editingContactId: this.contactId || null,
      editingContactName: this.contactName || null,

      dataset: [],
      columns: [
        { field: 'monthName', sortField: 'monthSortOrder' },
      ],
      rowFields: [
        { field: 'name', name: 'Name' }
      ],
      fields: [
        { field: 'total' }
      ],
    }
  },
  watch: {
    'startDate': function() {
      this.editingStartDate = this.startDate
    },
    'endDate': function() {
      this.editingEndDate = this.endDate
    },
    accountLocationId: function() {
      this.editingAccountLocationIdList = this.accountLocationId || []
    },
    contactId: function() {
      this.editingContactId = this.contactId || null
    },
    valueList: function() {
      this.generatePivotDataset()
    },
    monthList: function() {
      this.generatePivotDataset()
    }
  },
  computed: {
    ...mapState({
      taxName: state => state.account.salesTaxRate.name
    }),
    defaultColumnOrderIndex() {
      return this.monthList && this.monthList.length > 0 ? this.monthList.length + 1 : 0
    }
  },
  mounted() {
    this.generatePivotDataset()
  },
  methods: {
    applyOrderSummaryFilter() {
      this.$emit('applyFilter', { startDate: this.editingStartDate, endDate: this.editingEndDate, accountLocationId: this.editingAccountLocationIdList, contactId: this.editingContactId, contactName: this.editingContactName })
    },
    resetOrderSummaryFilter() {
      this.$emit('resetFilter')
    },
    exportDataCSV() {
      var summaryFilter = {
        startDate: this.startDate,
        endDate: this.endDate,
        maxGroupSize: 0,
        includeMonthBreakdown: true,
        exportReport: this.reportField,
        accountLocationId: this.accountLocationId,
        contactId: this.contactId,
        format: 'csv'
      }

      let queryString = buildUrl({ queryParams: summaryFilter })

      this.$apiService.reportingService.generateReportSignedURL(`/intelligence/orders/ordervalue/fullsummary${queryString}`)
      .then(response => {
        window.location.href = response.data.url
      })
    },
    generatePivotDataset() {
      this.dataset = []

      if(!this.valueList || !this.monthList || this.valueList.length == 0 || this.monthList.length == 0)
        return

      // Format data into dataset for pivot table
      this.valueList.forEach(valueInfo => {        
        valueInfo.monthList.forEach((monthInfo, index) => {
          let monthLookup = this.monthList.find(month => month.key == monthInfo.key)

          this.dataset.push({
            id: valueInfo.stockId,
            name: valueInfo.name,
            stockId: valueInfo.stockId,
            contactId: valueInfo.contactId,
            monthName: monthLookup ? monthLookup.title : '-',
            monthSortOrder: index,
            total: monthInfo.value
          })
        })

        // Add total column
        this.dataset.push({
          id: valueInfo.stockId,
          name: valueInfo.name,
          stockId: valueInfo.stockId,
          contactId: valueInfo.contactId,
          monthName: 'Total',
          monthSortOrder: 9999,
          total: valueInfo.value,
        })
      })
    }
  }
}
</script>